import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { images, octiotUrl } from "../common/images";
import jubilantLogo from "../../assets/jubilant-logo.svg";
import jubilantMark from "../../assets/jubilant-mark.svg";
import { isAdminRole } from "../redux/action/users";
import { config } from "../../config";
import { NAV_ITEMS, isPageActive } from "./pages";
import "./SideNav.css";

const COLLAPSED_KEY = "sideNavCollapsed";

// Remembered per browser. Storage can be missing or blocked, in which case the
// sidebar opens expanded on wide windows and collapsed on narrow ones.
const initialCollapsed = () => {
  try {
    const saved = window.localStorage.getItem(COLLAPSED_KEY);
    if (saved !== null) return saved === "true";
  } catch (error) {
    // Fall through to the window-width default.
  }
  return window.innerWidth < 1100;
};

function SideNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const login = useSelector((state) => state.login);
  const houseData = useSelector((state) => state.houseData);
  const isAdmin = isAdminRole(login?.profile?.roleName);
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  const toggle = () => {
    setCollapsed((current) => {
      try {
        window.localStorage.setItem(COLLAPSED_KEY, String(!current));
      } catch (error) {
        // Not remembered; the toggle still works for this visit.
      }
      return !current;
    });
  };

  const isOctiot = document.URL.includes(octiotUrl);
  const isPnb = config.pnbProperties.includes(houseData?._id);

  return (
    <aside className={collapsed ? "sideNav sideNavCollapsed" : "sideNav"}>
      <div className="sideNavBrand">
        {!collapsed && (
          <div className="sideNavLogos">
            <img
              src={isOctiot ? images.octiot : isPnb ? images.pnbLogo : jubilantLogo}
              alt={isOctiot ? "Octiot" : isPnb ? "PNB" : "Jubilant FoodWorks"}
              className={isOctiot || isPnb ? "sideNavLogo" : "sideNavLogo sideNavLogoJubilant"}
            />
          </div>
        )}
        {collapsed && !isOctiot ? (
          // Collapsed, the brand shrinks to the Jubilant mark, which also expands
          // the sidebar. The Octiot logo has no mark of its own, so it keeps
          // the menu icon.
          <button
            type="button"
            className="sideNavMark"
            onClick={toggle}
            aria-label="Expand navigation"
            title="Expand"
          >
            <img src={jubilantMark} alt="Jubilant FoodWorks" />
          </button>
        ) : (
          <button
            type="button"
            className="sideNavToggle"
            onClick={toggle}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <MenuOutlinedIcon fontSize="small" /> : <ChevronLeftOutlinedIcon fontSize="small" />}
          </button>
        )}
      </div>

      <nav className="sideNavItems">
        {NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin).map(({ label, to, paths, Icon }) => {
          const active = isPageActive({ paths }, pathname);
          return (
            <button
              key={to}
              type="button"
              className={active ? "sideNavItem sideNavItemActive" : "sideNavItem"}
              onClick={() => navigate(to)}
              title={collapsed ? label : undefined}
              aria-current={active ? "page" : undefined}
            >
              <Icon fontSize="small" />
              {!collapsed && <span className="sideNavLabel">{label}</span>}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

export default SideNav;
